import { useState, useEffect, useRef, createElement } from "react";
import { toast } from "sonner";
import { logger } from "@/shared/utils/logger";
import { ExportProgressToast } from "../components/ExportProgressToast";
import { formatEntryId, parseEntryId } from "@/v2/hooks/actions/entryId";

export type ExportProgress = {
  status: "idle" | "exporting" | "done";
  current: number;
  total: number;
  exportedIds: number[];
  failedEntries: { id: number; error: string }[];
};

const IDLE: ExportProgress = {
  status: "idle",
  current: 0,
  total: 0,
  exportedIds: [],
  failedEntries: [],
};

type Options = {
  makeApiRequest: (opts: {
    endpoint: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body: unknown;
  }) => Promise<{ data?: any; error?: any }>;
  onComplete?: (result: {
    exportedIds: number[];
    failedEntries: { id: number; error: string }[];
  }) => void;
  progressToastId?: string;
};

const EMPTY_SET = new Set<number>();

export function useExportEntries({ makeApiRequest, onComplete, progressToastId = "export-progress" }: Options) {
  const [progress, setProgress] = useState<ExportProgress>(IDLE);
  const [exportingIds, setExportingIds] = useState<Set<number>>(EMPTY_SET);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Progress toast
  useEffect(() => {
    if (progress.status === "exporting") {
      toast.custom(
        () =>
          createElement(ExportProgressToast, {
            current: progress.current,
            total: progress.total,
          }),
        {
          id: progressToastId,
          duration: Infinity,
          unstyled: true,
          className: "!backdrop-blur-none !bg-transparent !border-none !shadow-none !p-0",
          style: { zoom: 1.1 },
        },
      );
    } else {
      toast.dismiss(progressToastId);
    }
  }, [progress.status, progress.current, progress.total, progressToastId]);

  const exportEntries = async (ids: number[]) => {
    if (progress.status === "exporting") return;
    if (ids.length === 0) return;

    setExportingIds(new Set(ids));
    setProgress({
      status: "exporting",
      current: 0,
      total: ids.length,
      exportedIds: [],
      failedEntries: [],
    });

    let exportedIds: number[] = [];
    let failedEntries: { id: number; error: string }[] = [];

    try {
      const resp = await logger.trackOperation(
        "Export entries",
        async () => {
          const r = await makeApiRequest({
            endpoint: "/v2/entries/export",
            method: "POST",
            body: { ids: ids.map(formatEntryId) },
          });
          if (r.error) throw new Error(String(r.error));
          return r;
        },
        { entryIds: ids },
      );
      const data = resp?.data ?? {};
      exportedIds = (data.exportedIds ?? []).map(parseEntryId);
      failedEntries = (data.failedEntries ?? []).map(
        (entry: { id: string; error: string }) => ({
          id: parseEntryId(entry.id),
          error: entry.error,
        }),
      );
    } catch (e) {
      failedEntries = ids.map((id) => ({
        id,
        error: e instanceof Error ? e.message : "Export failed",
      }));
    } finally {
      setProgress((prev) => ({
        ...prev,
        current: ids.length,
        exportedIds: [...exportedIds],
        failedEntries: [...failedEntries],
      }));
    }

    setExportingIds(EMPTY_SET);
    setProgress((prev) => ({ ...prev, status: "done" }));
    onCompleteRef.current?.({ exportedIds, failedEntries });
  };

  const reset = () => setProgress(IDLE);

  return {
    progress,
    exportEntries,
    exportingIds,
    reset,
    isExporting: progress.status === "exporting",
  };
}
